import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import {
  Archive, ArrowRight, ArrowUpRight, ChevronRight, ClipboardCheck, MoreVertical, Pencil, RotateCcw, UserPlus, X,
} from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@repo/ui/src/components/ui/alert-dialog';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@repo/ui/src/components/ui/drawer';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@repo/ui/src/components/ui/dropdown-menu';
import { cn } from '@repo/ui/src/lib/utils';
import type { ClassRosterRow, ClassSlotRow } from '@repo/shared-types';
import { PageHeader, type PageHeaderAction } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { useCan } from '../../hooks/useCan';
import {
  useClass, useArchiveClass, useUnarchiveClass, useRemoveClassMember,
} from '../../hooks/useClasses';
import { useSlot } from '../../hooks/useSlots';
import { useMembers } from '../../hooks/useTeam';
import { useMonthlyReviewSummary } from '../../hooks/useProgress';
import { useServerNow } from '../../hooks/useServerNow';
import { resolveTeacher, type TeacherLike } from '../../lib/teacherDisplay';
import { classColorStyle } from '../../lib/classColors';
import { groupSlotsByTime } from '../../lib/classSchedule';
import { isPartialMembership } from '../../lib/classDisplay';
import { fmtHouseDate } from '../../lib/appliedDate';
import { dayKey, currentMonth, formatMonthKey } from '../../lib/sessionTime';
import { formatVnd } from '../../lib/formatVnd';
import { ClassFormSheet } from './ClassFormSheet';
import { EndClassDialog } from './EndClassDialog';
import { AddMembersDialog } from './AddMembersDialog';
import { MemberSlotsPopover, MemberSlotsForm } from './MemberSlotsPopover';
import { SlotFormDialog } from '../schedules/SlotFormDialog';

// i18next's TFunction isn't structurally assignable to a plain callback (see
// StudentsPage's own KhoaBadgeT note) — a plain signature instead, matching
// ClassListTab's own ListT.
type T = (key: string, options?: Record<string, unknown>) => string;

/** One khung giờ chip, read-only (README decision 8 / S5 frame 2): filled =
 *  member, hollow/dashed = not. The fill reuses the `sky` class-colour token
 *  as a fixed "membership" tint — it is NOT the class's own identity colour
 *  (that lives on the header's colour square), just the same infrastructure. */
function SlotChip({ label, on, size = 'md' }: { label: string; on: boolean; size?: 'md' | 'sm' }) {
  const filled = classColorStyle('sky')!;
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center rounded-md border font-semibold',
        size === 'md' ? 'h-[22.4px] min-w-[30px] px-[6.4px] text-[11.5px]' : 'h-[20.8px] min-w-[26px] px-[4.8px] text-[11px]',
        !on && 'border-dashed border-surface-300 text-surface-400',
      )}
      style={on ? filled : undefined}
    >
      {label}
    </span>
  );
}

function feeLabel(t: T, pricing: ClassRosterRow['pricing']): string {
  const isTerm = pricing.pricingType !== 'MONTHLY_FLAT' && pricing.billingPeriod === 'TERM';
  const label = pricing.pricingType === 'MONTHLY_FLAT'
    ? t('classForm.pricingMonthlyFlatLabel')
    : isTerm
      ? t('classForm.pricingSessionTermLabel')
      : t('classForm.pricingSessionMonthlyLabel');
  // Fix wave (review I12) — through the app's one currency formatter
  // instead of a baked-in "đ" suffix on a raw `toLocaleString` — matches
  // every other rate rendered on this branch (ConfirmTable, MemberSlots…).
  const rate = formatVnd(pricing.rate);
  // "100.000 ₫/20 buổi" — termSize is PER_SESSION TERM only; the follow-up
  // API round added it to `ClassRosterRow.pricing`.
  const suffix = isTerm && pricing.termSize != null ? `/${pricing.termSize} ${t('classDetail.roster.termUnit')}` : '';
  return `${label} · ${rate}${suffix}`;
}

function DesktopRosterRow({
  row, sortedSlots, groupId, canManage, onRemove,
}: {
  row: ClassRosterRow;
  sortedSlots: ClassSlotRow[];
  groupId: string;
  canManage: boolean;
  onRemove: (row: ClassRosterRow) => void;
}) {
  const { t } = useTranslation('scheduling');
  const [editOpen, setEditOpen] = useState(false);
  const joinedCount = row.slotIds.length;
  const partial = isPartialMembership(joinedCount, sortedSlots.length);

  return (
    <div
      className="group grid min-h-[46.4px] items-center gap-x-[12.8px] border-b border-surface-100 px-4 py-[6.4px] text-sm last:border-b-0"
      style={{ gridTemplateColumns: 'minmax(0, 1.8fr) 2.1fr 0.9fr 1.6fr 36px' }}
    >
      <span className="flex min-w-0 items-baseline gap-[6.4px]">
        <Link to={`/students/${row.studentId}`} className="whitespace-nowrap font-medium text-surface-800 hover:underline">
          {row.studentName}
        </Link>
        {row.nickname ? <span className="text-[12.5px] text-surface-500">({row.nickname})</span> : null}
      </span>
      <span className="flex items-center gap-[4.8px]">
        {sortedSlots.map((s) => (
          <SlotChip key={s.id} label={t(`days.${s.dayOfWeek}`)} on={row.slotIds.includes(s.id)} />
        ))}
        {partial ? (
          <span className="tabular ml-[4.8px] whitespace-nowrap text-[12px] text-surface-500">
            {t('classDetail.roster.partial', { joined: joinedCount, total: sortedSlots.length })}
          </span>
        ) : null}
        {canManage ? (
          <MemberSlotsPopover
            open={editOpen}
            onOpenChange={setEditOpen}
            groupId={groupId}
            member={row}
            slots={sortedSlots}
            trigger={
              <button
                type="button"
                aria-label={t('classDetail.roster.editSlotsAria', { name: row.studentName })}
                className="ml-[4.8px] flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-surface-100 text-surface-600 opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
              >
                <Pencil className="h-3 w-3" strokeWidth={2} />
              </button>
            }
          />
        ) : null}
      </span>
      <span className="tabular text-[13.5px] text-surface-600">{fmtHouseDate(row.joinedAt)}</span>
      <span className="text-[13.5px] text-surface-600">{feeLabel(t, row.pricing)}</span>
      {canManage ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t('classDetail.roster.rowMenuAria', { name: row.studentName })}
              className="flex h-[28.8px] w-[28.8px] items-center justify-center justify-self-end rounded-lg text-surface-500 hover:bg-surface-100"
            >
              <MoreVertical className="h-[14.4px] w-[14.4px]" strokeWidth={1.8} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => setEditOpen(true)} className="cursor-pointer">
              <Pencil className="mr-2 h-3.5 w-3.5" /> {t('classDetail.roster.editSlots')}
            </DropdownMenuItem>
            <DropdownMenuItem asChild className="cursor-pointer">
              <Link to={`/students/${row.studentId}`}>
                <ArrowUpRight className="mr-2 h-3.5 w-3.5" /> {t('classDetail.roster.openStudent')}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => onRemove(row)}
              className="cursor-pointer text-destructive focus:bg-destructive/10 focus:text-destructive"
            >
              <X className="mr-2 h-3.5 w-3.5" /> {t('classDetail.roster.removeFromClass')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <span />
      )}
    </div>
  );
}

function PhoneRosterRow({
  row, sortedSlots, canManage, onTap,
}: {
  row: ClassRosterRow;
  sortedSlots: ClassSlotRow[];
  canManage: boolean;
  onTap: () => void;
}) {
  const { t } = useTranslation('scheduling');
  const content = (
    <>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14px] font-medium text-surface-900">
          {row.studentName} {row.nickname ? <span className="font-normal text-surface-500">{row.nickname}</span> : null}
        </span>
        <span className="block text-[12.5px] text-surface-500">{feeLabel(t, row.pricing)}</span>
      </span>
      <span className="flex shrink-0 gap-[3.2px]">
        {sortedSlots.map((s) => (
          <SlotChip key={s.id} label={t(`days.${s.dayOfWeek}`)} on={row.slotIds.includes(s.id)} size="sm" />
        ))}
      </span>
      {canManage ? <ChevronRight className="h-4 w-4 shrink-0 text-surface-400" /> : null}
    </>
  );
  const rowClass = 'flex min-h-[44px] w-full items-center gap-[9.6px] border-b border-surface-100 px-1 py-[9.6px] text-left last:border-b-0';
  if (!canManage) {
    return <Link to={`/students/${row.studentId}`} className={rowClass}>{content}</Link>;
  }
  return (
    <button type="button" onClick={onTap} className={rowClass}>
      {content}
    </button>
  );
}

/** The phone roster row's action sheet (§6.3 — a phone bottom panel is a
 *  vaul Drawer): "Sửa khung giờ" swaps this drawer's own content for
 *  `MemberSlotsForm` rather than trying to anchor a floating Popover to a
 *  trigger inside a sheet that is mid-close — no such nested-panel pattern
 *  exists in this codebase, and the row has no persistent pencil at 390 to
 *  anchor one to anyway. */
function RosterActionsDrawer({
  row, groupId, sortedSlots, onClose, onRemove,
}: {
  row: ClassRosterRow | null;
  groupId: string;
  sortedSlots: ClassSlotRow[];
  onClose: () => void;
  onRemove: (row: ClassRosterRow) => void;
}) {
  const { t } = useTranslation('scheduling');
  const [editing, setEditing] = useState(false);

  return (
    <Drawer
      open={row !== null}
      onOpenChange={(o) => { if (!o) { onClose(); setEditing(false); } }}
    >
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle className={editing ? 'sr-only' : undefined}>{row?.studentName ?? ''}</DrawerTitle>
        </DrawerHeader>
        <div className="flex flex-col gap-1 px-5 pb-[calc(16px+env(safe-area-inset-bottom))]">
          {row && editing ? (
            <MemberSlotsForm
              groupId={groupId}
              member={row}
              slots={sortedSlots}
              onDone={() => { onClose(); setEditing(false); }}
            />
          ) : row ? (
            <>
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="flex min-h-[44px] items-center gap-[10px] text-[14px] text-surface-800"
              >
                <Pencil className="h-4 w-4" /> {t('classDetail.roster.editSlots')}
              </button>
              <Link to={`/students/${row.studentId}`} onClick={onClose} className="flex min-h-[44px] items-center gap-[10px] text-[14px] text-surface-800">
                <ArrowUpRight className="h-4 w-4" /> {t('classDetail.roster.openStudent')}
              </Link>
              <button
                type="button"
                onClick={() => { onRemove(row); onClose(); }}
                className="flex min-h-[44px] items-center gap-[10px] text-[14px] text-destructive"
              >
                <X className="h-4 w-4" /> {t('classDetail.roster.removeFromClass')}
              </button>
            </>
          ) : null}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

function SlotCard({
  slot, room, members, now,
  canManage, onEdit,
}: {
  slot: ClassSlotRow;
  room: string | null;
  members: TeacherLike[];
  now: Date;
  canManage: boolean;
  onEdit: () => void;
}) {
  const { t } = useTranslation('scheduling');
  const teacher = resolveTeacher(members, slot.teacherId);
  const next = slot.nextSession;
  const isToday = next ? dayKey(next.startAt) === dayKey(now.toISOString()) : false;
  const attention = isToday && next ? !next.attendanceMarked : false;

  return (
    <div className="flex flex-col gap-[4.8px] border-b border-surface-100 px-[14.4px] py-[11.2px] last:border-b-0">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-baseline gap-[6.4px] text-[14px]">
          <span className="font-semibold">
            {t(`days.${slot.dayOfWeek}`)} <span className="tabular">{slot.startTime}</span>
          </span>
          <span className="text-[13px] text-surface-500">{t('classDetail.slots.duration', { minutes: slot.durationMin })}</span>
        </span>
        {canManage ? (
          <button
            type="button"
            aria-label={t('classDetail.slots.editAria')}
            onClick={onEdit}
            className="flex h-6 w-6 items-center justify-center rounded-md text-surface-500 hover:bg-surface-100"
          >
            <Pencil className="h-[12.8px] w-[12.8px]" strokeWidth={1.8} />
          </button>
        ) : null}
      </div>
      {teacher ? (
        <div className="flex items-center gap-[4.8px] text-[12.5px] text-surface-600">
          <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: teacher.color }} />
          {teacher.name}
          {room ? <span>· {room}</span> : null}
        </div>
      ) : room ? (
        <div className="text-[12.5px] text-surface-600">{room}</div>
      ) : null}
      {next ? (
        <Link
          to={`/?date=${dayKey(next.startAt)}`}
          className={cn('inline-flex items-center gap-1 text-[12.5px] font-medium', attention ? 'text-attention-foreground' : 'text-brand-600')}
        >
          {attention
            ? t('classDetail.slots.nextToday', { time: slot.startTime })
            : t('classDetail.slots.next', { day: t(`days.${slot.dayOfWeek}`), date: fmtHouseDate(next.startAt, 'short') })}
          <ArrowRight className="h-3 w-3" />
        </Link>
      ) : null}
    </div>
  );
}

function ClassReviewCard({ subjectId, subjectName }: { subjectId: string; subjectName: string }) {
  const { t } = useTranslation('scheduling');
  const now = useServerNow();
  const month = currentMonth(now);
  const { data: rows } = useMonthlyReviewSummary(month);
  const row = rows?.find((r) => r.subjectId === subjectId);
  const pct = row && row.total > 0 ? Math.round((row.written / row.total) * 100) : 0;

  return (
    <section>
      <h2 className="font-display m-0 mb-[9.6px] text-[15px] font-semibold tracking-[-0.012em]">{t('classDetail.review.title')}</h2>
      <div className="flex flex-col gap-[6.4px] rounded-[10px] border border-surface-200 bg-card px-[14.4px] py-[12.8px]">
        <div className="flex items-center justify-between text-[14px]">
          <span className="font-semibold">{t('classDetail.review.monthTitle', { month: formatMonthKey(month) })}</span>
          {row ? (
            <span className="tabular text-surface-500">{t('classDetail.review.progress', { done: row.written, total: row.total })}</span>
          ) : null}
        </div>
        {row ? (
          <span className="block h-1 overflow-hidden rounded-full bg-surface-100">
            <span className="block h-full rounded-full bg-brand-500" style={{ width: `${pct}%` }} />
          </span>
        ) : null}
        <p className="m-0 text-[12.5px] text-surface-500">{t('classDetail.review.hint')}</p>
        <Link to={`/subjects/${subjectId}/monthly-reviews`} className="text-[13px] font-medium">
          {t('classDetail.review.link', { subject: subjectName })}
        </Link>
      </div>
    </section>
  );
}

/**
 * THI-267 Task 12 — `/classes/:id`, replacing Task 10's placeholder (spec §5
 * "ClassDetailPage" bullet, S5/S5b, README decisions 8-9). Header + roster +
 * right column, ported from `S5 - Chi tiet lop.dc.html` **frame 2 only**
 * (read-only chips + pencil/menu → popover; frame 1 is the rejected
 * toggle-chip alternative) and `S5b - Chi tiet lop MEMBER va 390.dc.html`
 * (frame 3 MEMBER, frame 4 390).
 *
 * Route carries NO `RequirePermission` (§4.5 pattern: MEMBER reads this page
 * — the mock's own frame 3 — so the guard belongs on individual write
 * affordances via `useCan('manage:slots')`, not on the element).
 */
export function ClassDetailPage() {
  const { t } = useTranslation('scheduling');
  const { id } = useParams<{ id: string }>();
  const { data: klass, isLoading, isError } = useClass(id ?? null);
  const canManage = useCan('manage:slots');

  // `ClassGroupDetail.status` (ACTIVE | ARCHIVED, orthogonal to the derived
  // `phase`) — the Task 12 follow-up that replaced the earlier client-side
  // cross-check against `useClasses({ status: 'ARCHIVED' })`.
  const isArchived = klass?.status === 'ARCHIVED';

  const { data: members } = useMembers({ includeRemoved: true });
  const now = useServerNow();

  const archive = useArchiveClass();
  const unarchive = useUnarchiveClass();
  const removeMember = useRemoveClassMember(id ?? '');

  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [addMembersOpen, setAddMembersOpen] = useState(false);
  const [endDialogOpen, setEndDialogOpen] = useState(false);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<ClassRosterRow | null>(null);
  const [phoneActionRow, setPhoneActionRow] = useState<ClassRosterRow | null>(null);
  // Right column's per-card pencil (S5's own "a pencil per card opening
  // SlotFormDialog for that single khung giờ" — a DIFFERENT edit surface
  // than the roster row's MemberSlotsPopover above). `ClassGroupDetail.slots`
  // is the lightweight `ClassSlotRow[]` (no members/teachers/subject/group),
  // so editing needs the full `ClassSlotWithDetail` SlotFormDialog expects —
  // fetched lazily via useSlot, matching TimetableGridTab's own edit flow.
  const [editSlotId, setEditSlotId] = useState<string | null>(null);
  const { data: editSlotDetail } = useSlot(editSlotId);

  const sortedSlots = useMemo(() => {
    if (!klass) return [];
    return [...klass.slots].sort((a, b) => a.dayOfWeek - b.dayOfWeek || a.startTime.localeCompare(b.startTime));
  }, [klass]);

  const activeRoster = useMemo(() => (klass?.roster ?? []).filter((r) => r.status === 'ACTIVE'), [klass]);
  const pausedRoster = useMemo(() => (klass?.roster ?? []).filter((r) => r.status === 'PAUSED'), [klass]);

  if (isLoading || !klass) {
    return (
      <div className="mx-auto w-full max-w-6xl p-6 sm:p-8">
        <PageHeader size="lg" back={{ label: t('classes.title'), to: '/classes' }} title={isError ? t('classes.list.loadError') : t('classes.list.loading')} />
      </div>
    );
  }

  const scheduleGroups = groupSlotsByTime(klass.slots);
  const duration = klass.slots[0]?.durationMin;
  const teachers = klass.teacherIds.map((tid) => resolveTeacher(members ?? [], tid)).filter((x): x is NonNullable<typeof x> => x !== null);

  // Fix wave (review M2) — ENDED used to render in `text-attention-foreground`
  // (ochre), inconsistent with `ClassListTab`'s own rendering of the same
  // derived state (`text-surface-500`) and against §6.15 (ochre fires only
  // when a ceiling BLOCKS — a class having ended blocks nothing here).
  const stateLine = klass.phase === 'ENDED'
    ? { text: t('classDetail.header.ended', { date: fmtHouseDate(klass.endsOn ?? klass.startsOn) }), tone: 'muted' as const }
    : klass.phase === 'UPCOMING'
      ? { text: t('classDetail.header.upcoming', { date: fmtHouseDate(klass.startsOn, 'short') }), tone: 'brand' as const }
      : null;

  const actions: PageHeaderAction[] = canManage
    ? [
      {
        key: 'add-members',
        label: t('classDetail.header.addMembers'),
        icon: UserPlus,
        onClick: () => setAddMembersOpen(true),
      },
      { key: 'edit-class', label: t('classDetail.header.editClass'), icon: Pencil, variant: 'outline', onClick: () => setEditSheetOpen(true) },
      ...(!isArchived && klass.phase !== 'ENDED'
        ? [{ key: 'end-class', label: t('classDetail.header.endClass'), onClick: () => setEndDialogOpen(true) }]
        : []),
      isArchived
        ? { key: 'unarchive', label: t('classDetail.header.unarchive'), icon: RotateCcw, onClick: () => unarchive.mutate(klass.id) }
        : { key: 'archive', label: t('classDetail.header.archive'), icon: Archive, destructive: true, onClick: () => setArchiveConfirmOpen(true) },
    ]
    : [{ key: 'attendance-month', label: t('classDetail.header.attendanceMonth'), icon: ClipboardCheck, variant: 'outline', to: `/attendance?subject=${klass.subject.id}` }];

  return (
    <div className="mx-auto w-full max-w-6xl p-6 sm:p-8">
      <PageHeader
        size="lg"
        back={{ label: t('classes.title'), to: '/classes' }}
        leading={
          <span
            className={cn('h-4 w-4 shrink-0 rounded-[4px] border', !klass.color && 'border-dashed border-surface-300')}
            style={classColorStyle(klass.color) ?? undefined}
            aria-hidden
          />
        }
        title={klass.displayName}
        badge={
          <span className="inline-flex items-center rounded-full bg-surface-100 px-[9.6px] py-[1.6px] text-[12px] font-semibold text-surface-600">
            {klass.subject.name}
          </span>
        }
        subtitleWrap
        subtitle={
          <span className="flex flex-col gap-1">
            <span className="flex flex-wrap items-center gap-x-[12.8px] gap-y-[6.4px]">
              {scheduleGroups.map((g) => (
                <span key={g.time} className="inline-flex items-center gap-[3.2px]">
                  {g.days.map((d) => (
                    <span key={d} className="inline-flex h-[20.8px] items-center rounded-md bg-surface-100 px-[6.4px] text-[11.5px] font-semibold text-surface-600">
                      {t(`days.${d}`)}
                    </span>
                  ))}
                  <span className="tabular ml-[4.8px]">
                    {g.time}{duration != null ? ` · ${t('classDetail.slots.duration', { minutes: duration })}` : ''}
                  </span>
                </span>
              ))}
              {teachers.length > 0 ? (
                <>
                  <span>·</span>
                  <span className="flex flex-wrap items-center gap-x-[6.4px]">
                    {teachers.map((teacher, i) => (
                      <span key={i} className="inline-flex items-center gap-[4.8px]">
                        <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: teacher.color }} />
                        {teacher.name}
                      </span>
                    ))}
                  </span>
                </>
              ) : null}
              {klass.room ? (<><span>·</span><span>{klass.room}</span></>) : null}
              <span>·</span>
              <span>
                {klass.endsOn
                  ? t('classDetail.header.window', { start: fmtHouseDate(klass.startsOn), end: fmtHouseDate(klass.endsOn) })
                  : t('classDetail.header.windowOpen', { start: fmtHouseDate(klass.startsOn) })}
              </span>
              <span>·</span>
              <span>
                {t('classDetail.header.roster', { count: klass.maxCapacity != null ? `${klass.rosterCount}/${klass.maxCapacity}` : String(klass.rosterCount) })}
              </span>
            </span>
            {stateLine ? (
              <span className={cn('text-[12.5px] font-medium', stateLine.tone === 'muted' ? 'text-surface-500' : 'text-brand-600')}>
                {stateLine.text}
              </span>
            ) : null}
          </span>
        }
        actions={actions}
      />

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_336px]">
        <section className="order-2 lg:order-1">
          <div className="mb-[9.6px] flex items-baseline justify-between">
            <h2 className="font-display m-0 text-[15px] font-semibold tracking-[-0.012em]">
              {t('classDetail.roster.title')} <span className="font-normal text-surface-500">({activeRoster.length})</span>
            </h2>
          </div>

          {activeRoster.length === 0 && pausedRoster.length === 0 ? (
            <EmptyState kind="teach" icon={UserPlus} title={t('classDetail.roster.emptyTitle')} body={t('classDetail.roster.emptyBody')} />
          ) : (
            <>
              <div className="hidden overflow-hidden rounded-[10px] border border-surface-200 bg-card md:block">
                <div
                  className="grid h-[35.2px] items-center gap-x-[12.8px] border-b border-surface-200 px-4 text-[11px] font-semibold uppercase tracking-wide text-surface-500"
                  style={{ gridTemplateColumns: 'minmax(0, 1.8fr) 2.1fr 0.9fr 1.6fr 36px' }}
                >
                  <span>{t('classDetail.roster.columnStudent')}</span>
                  <span>{t('classDetail.roster.columnSlots')}</span>
                  <span>{t('classDetail.roster.columnStart')}</span>
                  <span>{t('classDetail.roster.columnFee')}</span>
                  <span />
                </div>
                {activeRoster.map((row) => (
                  <DesktopRosterRow
                    key={row.enrollmentId}
                    row={row}
                    sortedSlots={sortedSlots}
                    groupId={klass.id}
                    canManage={canManage}
                    onRemove={setRemoveTarget}
                  />
                ))}
              </div>

              <div className="rounded-[10px] border border-surface-200 bg-card md:hidden">
                {activeRoster.map((row) => (
                  <PhoneRosterRow
                    key={row.enrollmentId}
                    row={row}
                    sortedSlots={sortedSlots}
                    canManage={canManage}
                    onTap={() => setPhoneActionRow(row)}
                  />
                ))}
              </div>
            </>
          )}

          {pausedRoster.length > 0 ? (
            <div className="mt-4">
              <div className="mb-[6.4px] text-[12px] font-semibold uppercase tracking-wide text-surface-400">
                {t('slot.pausedSection')} ({pausedRoster.length})
              </div>
              <div className="flex flex-col gap-[6.4px]">
                {pausedRoster.map((r) => (
                  <div
                    key={r.enrollmentId}
                    className="flex items-center gap-[12.8px] rounded-[10px] border border-surface-200 bg-surface-50 px-4 py-[9.6px] text-sm text-surface-500"
                  >
                    <span className="font-medium">{r.studentName}</span>
                    <span className="rounded-full bg-surface-200 px-2 py-[1.6px] text-[11.5px] font-semibold text-surface-700">{t('slot.pausedTag')}</span>
                    <span className="text-[13px]">{t('classDetail.roster.pausedNote')}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </section>

        <aside className="order-1 flex flex-col gap-5 lg:order-2">
          <section>
            <h2 className="font-display m-0 mb-[9.6px] text-[15px] font-semibold tracking-[-0.012em]">
              {t('classDetail.slots.title')} <span className="font-normal text-surface-500">({sortedSlots.length})</span>
            </h2>
            <div className="rounded-[10px] border border-surface-200 bg-card">
              {sortedSlots.map((s) => (
                <SlotCard
                  key={s.id}
                  slot={s}
                  room={klass.room}
                  members={members ?? []}
                  now={now}
                  canManage={canManage}
                  onEdit={() => setEditSlotId(s.id)}
                />
              ))}
            </div>
          </section>
          <ClassReviewCard subjectId={klass.subject.id} subjectName={klass.subject.name} />
        </aside>
      </div>

      {/* Owner/admin dialogs — mounted unconditionally (rules of hooks
          notwithstanding, these are components, not hooks), gated by their
          own `open` state so a MEMBER never sees them render live. */}
      <ClassFormSheet open={editSheetOpen} onOpenChange={setEditSheetOpen} classId={klass.id} />
      <AddMembersDialog open={addMembersOpen} onOpenChange={setAddMembersOpen} classId={klass.id} />
      <EndClassDialog open={endDialogOpen} onOpenChange={setEndDialogOpen} classId={klass.id} />

      <AlertDialog open={archiveConfirmOpen} onOpenChange={setArchiveConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('classDetail.archiveConfirm.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('classDetail.archiveConfirm.body')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('slot.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => archive.mutate(klass.id, { onSuccess: () => setArchiveConfirmOpen(false) })}
              disabled={archive.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('classDetail.archiveConfirm.action')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={removeTarget !== null} onOpenChange={(o) => { if (!o) setRemoveTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('classDetail.removeConfirm.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('classDetail.removeConfirm.body', { name: removeTarget?.studentName ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('slot.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!removeTarget) return;
                removeMember.mutate(removeTarget.enrollmentId, { onSuccess: () => setRemoveTarget(null) });
              }}
              disabled={removeMember.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('classDetail.removeConfirm.action')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Right column's per-khung-giờ pencil, once its full detail has
          loaded — only mounted once `editSlotDetail` resolves so
          SlotFormDialog's lazy initializers (keyed to `slot`) never seed
          from an undefined slot and render as a phantom "create" form for a
          frame (same reasoning as its own key-per-slot comment in
          TimetableGridTab). */}
      {editSlotDetail ? (
        <SlotFormDialog
          key={editSlotDetail.id}
          open
          onOpenChange={(o) => { if (!o) setEditSlotId(null); }}
          slot={editSlotDetail}
        />
      ) : null}

      <RosterActionsDrawer
        row={phoneActionRow}
        groupId={klass.id}
        sortedSlots={sortedSlots}
        onClose={() => setPhoneActionRow(null)}
        onRemove={setRemoveTarget}
      />
    </div>
  );
}
